import type {
  ContributorDto,
  ContributorRole,
  RoleDto,
  RoleSlotDto,
} from '@/types/dtos/contributors/response/contributor.dto';
import type { RemoteSpeakerDayDto } from '@/types/dtos/contributors/request/configure-remote-speaker.dto';
import type { FlowContributorStatusIconColor } from '@/utils/contributor-extraction.utils';

const normalizeId = (value?: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null && 'id' in value) {
    const id = (value as { id?: string }).id;
    return id ?? undefined;
  }
  return undefined;
};

export type RemoteSpeakerDayStatus = 'inactive' | 'pending' | 'active';

export function contributorHasActiveRemoteSpeakerDays(
  contributor: Pick<ContributorDto, 'remoteSpeakerDays'> | undefined,
): boolean {
  return contributor?.remoteSpeakerDays?.some((entry) => entry.status === 'active') ?? false;
}

export function countActiveRemoteSpeakerDays(
  contributor: Pick<ContributorDto, 'remoteSpeakerDays'> | undefined,
): number {
  return contributor?.remoteSpeakerDays?.filter((entry) => entry.status === 'active').length ?? 0;
}

/** Max purchasable remote speaker days = event days minus already-active entitlements for this contributor. */
export function maxRemoteSpeakerDayPurchaseQuantity(
  eventDayCount: number,
  contributor: Pick<ContributorDto, 'remoteSpeakerDays'> | undefined,
): number {
  return Math.max(0, eventDayCount - countActiveRemoteSpeakerDays(contributor));
}

export function clampRemoteSpeakerDayCheckoutQuantity(
  quantity: number,
  eventDayCount: number,
  contributor: Pick<ContributorDto, 'remoteSpeakerDays'> | undefined,
): number {
  const max = maxRemoteSpeakerDayPurchaseQuantity(eventDayCount, contributor);
  if (max <= 0) {
    return 0;
  }
  if (!Number.isFinite(quantity) || quantity < 1) {
    return 1;
  }
  return Math.min(quantity, max);
}

export function resolveDayIdForEventSlot(
  event: { slots?: Array<{ id: string; origin?: string; day?: string | { id: string } }> } | undefined,
  eventSlotId: string | undefined,
): string | undefined {
  if (!event?.slots?.length || !eventSlotId) {
    return undefined;
  }
  const normalizedEventSlotId = normalizeId(eventSlotId);
  const slot = event.slots.find((entry) => {
    const slotId = normalizeId(entry.id);
    const slotOrigin = normalizeId(entry.origin);
    return slotId === normalizedEventSlotId || slotOrigin === normalizedEventSlotId;
  });
  if (!slot?.day) {
    return undefined;
  }
  return typeof slot.day === 'string' ? slot.day : slot.day.id;
}

export function getRemoteSpeakerDayStatus(
  remoteSpeakerEnabled: boolean | undefined,
  remoteSpeakerDays: RemoteSpeakerDayDto[] | undefined,
  dayId: string | undefined,
  options?: { slotIsRemote?: boolean },
): RemoteSpeakerDayStatus {
  if (!dayId) {
    return 'inactive';
  }
  const hasRemoteContext = remoteSpeakerEnabled === true || options?.slotIsRemote === true;
  if (!hasRemoteContext) {
    return 'inactive';
  }
  const entry = remoteSpeakerDays?.find((day) => day.dayId === dayId);
  if (!entry) {
    return 'inactive';
  }
  if (entry.status === 'active') {
    return 'active';
  }
  if (entry.status === 'pending') {
    return 'pending';
  }
  return 'inactive';
}

export function slotNeedsRemoteSpeakerConfirmation(
  slot: Pick<RoleSlotDto, 'isRemoteSpeaker' | 'remoteSpeakerAcceptedAt'>,
): boolean {
  return slot.isRemoteSpeaker === true && !slot.remoteSpeakerAcceptedAt;
}

/** Per-contributor row header icon when `isRemoteSpeaker` is configured on that line. */
export function resolveContributorRowRemoteSpeakerIconColor(params: {
  isRemoteSpeakerConfigured: boolean;
  dayStatus: RemoteSpeakerDayStatus;
  /** Slot remote speakership confirmed (`remoteSpeakerAcceptedAt`), not role/slot invite acceptance. */
  remoteConfirmed: boolean;
}): FlowContributorStatusIconColor | null {
  if (!params.isRemoteSpeakerConfigured) {
    return null;
  }
  if (params.dayStatus === 'pending') {
    return 'primary';
  }
  if (params.dayStatus === 'active') {
    return params.remoteConfirmed ? 'success' : 'info';
  }
  return null;
}

/** Event / flow slot list aggregate — remote dimension only (slots with `isRemoteSpeaker`). */
export function resolveRemoteSpeakerAggregateIconColor(params: {
  dayStatus: RemoteSpeakerDayStatus;
  remoteConfirmed: boolean;
  applies: boolean;
}): FlowContributorStatusIconColor | null {
  if (!params.applies || params.dayStatus === 'inactive') {
    return null;
  }
  if (params.dayStatus === 'pending') {
    return 'primary';
  }
  return params.remoteConfirmed ? 'success' : 'info';
}

const COLOR_SEVERITY: Record<FlowContributorStatusIconColor, number> = {
  disabled: 0,
  action: 1,
  success: 2,
  info: 3,
  primary: 4,
  warning: 5,
};

export function worstContributorStatusColor(
  ...colors: (FlowContributorStatusIconColor | null | undefined)[]
): FlowContributorStatusIconColor {
  let worst: FlowContributorStatusIconColor = 'disabled';
  for (const color of colors) {
    if (!color) {
      continue;
    }
    if (COLOR_SEVERITY[color] > COLOR_SEVERITY[worst]) {
      worst = color;
    }
  }
  return worst;
}

export function resolveRemoteSpeakerAggregateForRoleSlots(
  slots: RoleSlotDto[],
  contributor: ContributorDto,
  dayId: string | undefined,
): FlowContributorStatusIconColor | null {
  const dayStatus = getRemoteSpeakerDayStatus(contributor.remoteSpeakerEnabled, contributor.remoteSpeakerDays, dayId);
  const remoteSlots = slots.filter((slot) => slot.deleted !== true && slot.isRemoteSpeaker === true);
  if (remoteSlots.length === 0) {
    return null;
  }

  const colors = remoteSlots.map((slot) =>
    resolveRemoteSpeakerAggregateIconColor({
      applies: true,
      dayStatus,
      remoteConfirmed: Boolean(slot.remoteSpeakerAcceptedAt),
    }),
  );
  return worstContributorStatusColor(...colors);
}

export function collectRemoteSpeakerAggregateColorForContext({
  allContributors,
  event,
  eventSlotId,
  flowSlotId,
  flowId,
  sessionId,
}: {
  allContributors: ContributorDto[];
  event?: { slots?: Array<{ id: string; day?: string | { id: string } }> };
  eventSlotId?: string;
  flowSlotId?: string;
  flowId?: string;
  sessionId?: string;
}): FlowContributorStatusIconColor | null {
  const dayId = resolveDayIdForEventSlot(event, eventSlotId);
  const colors: (FlowContributorStatusIconColor | null)[] = [];

  for (const contributor of allContributors) {
    for (const role of contributor.roles ?? []) {
      const matchingSlots = (role.slots ?? []).filter((slot) => {
        if (slot.deleted === true || slot.isRemoteSpeaker !== true) {
          return false;
        }
        if (flowSlotId) {
          const entityId = typeof slot.entity === 'string' ? slot.entity : slot.entity?.id;
          return entityId === flowSlotId;
        }
        if (eventSlotId) {
          return (
            normalizeId(slot.eventSlotId) === normalizeId(eventSlotId) ||
            (slot.entityType === 'EventSlot' && normalizeId(slot.entity as string) === normalizeId(eventSlotId))
          );
        }
        if (flowId && role.entityType === 'Flow') {
          const entityId = typeof role.entity === 'string' ? role.entity : role.entity?.id;
          return entityId === flowId;
        }
        if (sessionId && role.entityType === 'Session') {
          const entityId = typeof role.entity === 'string' ? role.entity : role.entity?.id;
          return entityId === sessionId;
        }
        return false;
      });

      if (matchingSlots.length > 0) {
        colors.push(resolveRemoteSpeakerAggregateForRoleSlots(matchingSlots, contributor, dayId));
      }
    }
  }

  return worstContributorStatusColor(...colors);
}

export function resolveContributorLineRemoteSpeakerStatus({
  contributor,
  roleSlot,
  event,
  eventSlotId,
}: {
  contributor: ContributorDto;
  role: RoleDto<ContributorRole>;
  roleSlot?: RoleSlotDto;
  event?: { slots?: Array<{ id: string; day?: string | { id: string } }> };
  eventSlotId?: string;
  /** @deprecated Unused — color uses `remoteSpeakerAcceptedAt`, not invite acceptance. */
  useRoleAcceptance?: boolean;
}): {
  showIcon: boolean;
  color: FlowContributorStatusIconColor | null;
  dayId?: string;
  dayStatus: RemoteSpeakerDayStatus;
} {
  const isRemoteConfigured = roleSlot ? roleSlot.isRemoteSpeaker === true : false;
  if (!isRemoteConfigured) {
    return { showIcon: false, color: null, dayId: undefined, dayStatus: 'inactive' };
  }

  const slotEventSlotId = roleSlot?.eventSlotId ?? eventSlotId;
  const dayId = resolveDayIdForEventSlot(event, slotEventSlotId);
  const dayStatus = getRemoteSpeakerDayStatus(contributor.remoteSpeakerEnabled, contributor.remoteSpeakerDays, dayId, {
    slotIsRemote: isRemoteConfigured,
  });

  const color = resolveContributorRowRemoteSpeakerIconColor({
    isRemoteSpeakerConfigured: true,
    dayStatus,
    remoteConfirmed: Boolean(roleSlot?.remoteSpeakerAcceptedAt),
  });

  return { showIcon: true, color, dayId, dayStatus };
}

/** Contributor row header — event-level remote speakership day entitlements. */
export function resolveContributorEventRemoteSpeakerHeaderIcon(params: {
  remoteSpeakerEnabled?: boolean;
  remoteSpeakerDays?: RemoteSpeakerDayDto[];
}): { showIcon: boolean; color: Extract<FlowContributorStatusIconColor, 'primary' | 'success'> | null } {
  if (params.remoteSpeakerEnabled !== true) {
    return { showIcon: false, color: null };
  }

  const days = params.remoteSpeakerDays ?? [];
  if (days.length === 0) {
    return { showIcon: true, color: null };
  }

  if (days.some((day) => day.status === 'pending')) {
    return { showIcon: true, color: 'primary' };
  }

  if (days.every((day) => day.status === 'active')) {
    return { showIcon: true, color: 'success' };
  }

  return { showIcon: true, color: null };
}

/** Whether the contributor is configured as a remote speaker at event level. */
export function contributorIsRemoteSpeakerInGeneral(contributor: ContributorDto): boolean {
  return contributor.remoteSpeakerEnabled === true;
}

export function resolveContributorSlotRemoteSpeakerListStatus(params: {
  contributor: ContributorDto;
  role: RoleDto<ContributorRole>;
  roleSlot: RoleSlotDto;
  event?: { slots?: Array<{ id: string; day?: string | { id: string } }> };
}): {
  showIcon: boolean;
  slotIsRemote: boolean;
  color: FlowContributorStatusIconColor | null;
  dayId?: string;
  dayStatus: RemoteSpeakerDayStatus;
  openEnableModalOnClick: boolean;
} {
  const slotIsRemote = params.roleSlot.isRemoteSpeaker === true;
  const remoteInGeneral = contributorIsRemoteSpeakerInGeneral(params.contributor);

  if (!remoteInGeneral && !slotIsRemote) {
    return {
      showIcon: false,
      slotIsRemote: false,
      color: null,
      dayStatus: 'inactive',
      openEnableModalOnClick: false,
    };
  }

  if (!slotIsRemote) {
    return {
      showIcon: remoteInGeneral,
      slotIsRemote: false,
      color: 'action',
      dayStatus: 'inactive',
      openEnableModalOnClick: remoteInGeneral,
    };
  }

  const lineStatus = resolveContributorLineRemoteSpeakerStatus({
    contributor: params.contributor,
    role: params.role,
    roleSlot: params.roleSlot,
    event: params.event,
    eventSlotId: params.roleSlot.eventSlotId,
    useRoleAcceptance: false,
  });

  return {
    showIcon: true,
    slotIsRemote: true,
    color: lineStatus.color,
    dayId: lineStatus.dayId,
    dayStatus: lineStatus.dayStatus,
    openEnableModalOnClick: false,
  };
}
